import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Check } from 'lucide-react';
import { Button } from '../ui/Button.js';
import { parseApplyBlock } from './parseApplyBlock.js';
import { Toggle } from '../ui/Toggle.js';
import {
  buildWorkflowAssistantReviewSections,
  selectWorkflowAssistantPatch,
} from '../../lib/workflow-assistant-review.js';

interface WorkflowAssistantApplyPanelProps<FormValues extends Record<string, unknown>> {
  latestMessage: string;
  onApplyFieldUpdate: (partial: Partial<FormValues>) => void;
}

export function WorkflowAssistantApplyPanel<FormValues extends Record<string, unknown>>({
  latestMessage,
  onApplyFieldUpdate,
}: WorkflowAssistantApplyPanelProps<FormValues>) {
  const parsed = parseApplyBlock(latestMessage);
  const sections = useMemo(() => (parsed ? buildWorkflowAssistantReviewSections(parsed) : []), [parsed]);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const expectedWorkflowPatch = latestMessage.trim().startsWith('{');

  useEffect(() => {
    setChecked(new Set(sections.filter((section) => section.applyable !== false).map((section) => section.id)));
  }, [sections]);

  if (!parsed) {
    if (!expectedWorkflowPatch) return null;
    return (
      <div className="mt-2 flex items-start gap-2 rounded-lg border border-warning/30 bg-warning-muted/30 p-3 text-xs text-warning">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        <span>The assistant returned workflow changes, but they were not valid reviewable JSON.</span>
      </div>
    );
  }
  if (sections.length === 0) return null;

  const selectedCount = sections.filter((section) => checked.has(section.id)).length;
  const itemCount = sections
    .filter((section) => checked.has(section.id))
    .reduce((sum, section) => sum + section.itemCount, 0);

  const toggleSection = (id: string) => {
    setChecked((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleApply = () => {
    const patch = selectWorkflowAssistantPatch(sections, checked);
    if (Object.keys(patch).length > 0) onApplyFieldUpdate(patch as Partial<FormValues>);
  };

  return (
    <div className="mt-2 rounded-lg border border-card-border bg-background-gray-secondary_alt p-3">
      <p className="mb-2 text-xs font-medium text-muted">Workflow proposals ready for review</p>
      <div className="space-y-1.5">
        {sections.map((section) => (
          <div
            key={section.id}
            className={`flex items-start gap-2 rounded p-1.5 ${
              section.applyable === false ? 'opacity-75' : 'hover:bg-background-gray-secondary'
            }`}
          >
            <Toggle
              size="sm"
              checked={checked.has(section.id)}
              disabled={section.applyable === false}
              onChange={() => toggleSection(section.id)}
              aria-label={`Apply ${section.title}`}
            />
            <span className="min-w-0 flex-1">
              <span className="block text-xs font-medium text-heading">{section.title}</span>
              <span className="mt-0.5 block truncate text-xs text-muted">{section.detail}</span>
              {section.applyable === false && (
                <span className="mt-0.5 block text-[10px] text-warning">
                  Requires an explicit workflow step decision.
                </span>
              )}
              {section.id === 'llm-transform-proposals' && section.applyable !== false && (
                <span className="mt-0.5 block text-[10px] text-warning">
                  Adds template-backed semantic transform steps. Review selected inputs and output contracts before
                  saving.
                </span>
              )}
            </span>
          </div>
        ))}
      </div>
      <div className="mt-2 flex items-center justify-between gap-3">
        <span className="text-xs text-text-tertiary">
          {selectedCount} of {sections.length} proposal groups selected
        </span>
        <Button size="sm" variant="primary" disabled={selectedCount === 0} onClick={handleApply}>
          <Check size={12} />
          Accept {itemCount > 0 ? `(${itemCount})` : ''}
        </Button>
      </div>
    </div>
  );
}
